import React, { useEffect, useRef, useState } from 'react';
import {
  Blocking,
  Duration,
  Frequency,
  Friction,
  Predict,
  SegmentStatement,
  StatementType,
} from '../../types/processEdge';
import { useSessionStore } from '../../store/sessionStore';
import { validateProcessSegments } from '../../utils/processValidation';
import { buildEdgeMap, resolveProcessEdges } from '../../utils/processEdges';
import { helperTextByFrequency, normalizeStatement, statementTypeOptions, statementTypeToFlowType } from './segmentEditor/config';
import {
  actionButtonStyle,
  btn,
  chipGridStyle,
  contextGridStyle,
  divider,
  ghostButtonStyle,
  headerCardStyle,
  hintStyle,
  inputStyle,
  label,
  monoInputStyle,
  sectionStyle,
  sectionTitleStyle,
  tagStyle,
} from './segmentEditor/styles';

interface SegmentEditorProps {
  procId: number;
  segIdx: number;
  highlight?: boolean;
  onClose: () => void;
}

const normalizeFriction = (value: unknown): Friction => {
  if (value === 'low' || value === 'medium' || value === 'high' || value === 'none') return value;
  if (value === 'mid') return 'medium';
  return 'none';
};

const SegmentEditor: React.FC<SegmentEditorProps> = ({ procId, segIdx, highlight = false, onClose }) => {
  const processes = useSessionStore(s => s.processes);
  const edges = useSessionStore(s => s.edges);
  const updateSegment = useSessionStore(s => s.updateSegment);
  const lastValidationWarning = useSessionStore(s => s.lastValidationWarning);
  const setLastValidationWarning = useSessionStore(s => s.setLastValidationWarning);

  const proc = processes.find(p => p.id === procId);
  const seg = proc ? resolveProcessEdges(proc, buildEdgeMap(edges))[segIdx] : undefined;

  const statement = normalizeStatement(seg?.statement, seg?.from ?? '', seg?.to ?? '');
  const [segLabel, setSegLabel] = useState<string>(seg?.label || '')
  const [statementType, setStatementType] = useState<StatementType>(statement.type);
  const [stepAction, setStepAction] = useState(statement.type === 'STEP' ? statement.action ?? '' : '');
  const [stepSync, setStepSync] = useState<Blocking>(statement.type === 'STEP' ? (statement.sync ?? 'blocking') : 'blocking');
  const [stepMeta, setStepMeta] = useState(statement.type === 'STEP' ? JSON.stringify(statement.meta ?? {}) : '{}');

  const [assignVariable, setAssignVariable] = useState(statement.type === 'ASSIGN' ? statement.variable : '');
  const [assignExpression, setAssignExpression] = useState(statement.type === 'ASSIGN' ? statement.expression : '');

  const [waitMode, setWaitMode] = useState(statement.type === 'WAIT' ? statement.mode : 'FOR');
  const [waitDuration, setWaitDuration] = useState(statement.type === 'WAIT' ? (statement.duration ?? '') : '');
  const [waitCondition, setWaitCondition] = useState(statement.type === 'WAIT' ? (statement.condition ?? '') : '');

  const [callName, setCallName] = useState(statement.type === 'CALL' ? statement.name : '');
  const [callArgs, setCallArgs] = useState(statement.type === 'CALL' ? statement.args : '');

  const [triggerName, setTriggerName] = useState(statement.type === 'TRIGGER' ? statement.name : '');
  const [triggerArgs, setTriggerArgs] = useState(statement.type === 'TRIGGER' ? statement.args : '');

  const [onEvent, setOnEvent] = useState(statement.type === 'ON' ? statement.event : '');
  const [onChildBlock, setOnChildBlock] = useState(statement.type === 'ON' ? statement.childBlock : '');

  const [exceptionError, setExceptionError] = useState(statement.type === 'EXCEPTION' ? statement.error : '');
  const [exceptionChildBlock, setExceptionChildBlock] = useState(statement.type === 'EXCEPTION' ? statement.childBlock : '');

  const [friction, setFriction] = useState<Friction>(normalizeFriction(seg?.friction));

  const [freq, setFreq] = useState<Frequency>(seg?.freq ?? 'adhoc');
  const [dur, setDur] = useState<Duration>(seg?.dur ?? 'mins');
  const [pred, setPred] = useState<Predict>(seg?.pred ?? 'yes');
  const [error, setError] = useState('');

  const verbRef = useRef<HTMLInputElement>(null);
  const segLabelbRef = useRef<HTMLInputElement>(null);
 
  useEffect(() => {
    if (!seg) return;
    const currentStatement = normalizeStatement(seg.statement, seg.from, seg.to);
    setStatementType(currentStatement.type);
    setStepAction(currentStatement.type === 'STEP' ? currentStatement.action ?? '' : '');
    setStepSync(currentStatement.type === 'STEP' ? (currentStatement.sync ?? 'blocking') : 'blocking');
    setStepMeta(currentStatement.type === 'STEP' ? JSON.stringify(currentStatement.meta ?? {}) : '{}');
    setAssignVariable(currentStatement.type === 'ASSIGN' ? currentStatement.variable : '');
    setAssignExpression(currentStatement.type === 'ASSIGN' ? currentStatement.expression : '');
    setWaitMode(currentStatement.type === 'WAIT' ? currentStatement.mode : 'FOR');
    setWaitDuration(currentStatement.type === 'WAIT' ? (currentStatement.duration ?? '') : '');
    setWaitCondition(currentStatement.type === 'WAIT' ? (currentStatement.condition ?? '') : '');
    setCallName(currentStatement.type === 'CALL' ? currentStatement.name : '');
    setCallArgs(currentStatement.type === 'CALL' ? currentStatement.args : '');
    setTriggerName(currentStatement.type === 'TRIGGER' ? currentStatement.name : '');
    setTriggerArgs(currentStatement.type === 'TRIGGER' ? currentStatement.args : '');
    setOnEvent(currentStatement.type === 'ON' ? currentStatement.event : '');
    setOnChildBlock(currentStatement.type === 'ON' ? currentStatement.childBlock : '');
    setExceptionError(currentStatement.type === 'EXCEPTION' ? currentStatement.error : '');
    setExceptionChildBlock(currentStatement.type === 'EXCEPTION' ? currentStatement.childBlock : '');
    setFriction(normalizeFriction(seg.friction));
    setFreq(seg.freq ?? 'adhoc');
    setDur(seg.dur ?? 'mins');
    setPred(seg.pred ?? 'yes');
    setError('');
    setTimeout(() => {
      verbRef.current?.focus();
      verbRef.current?.select();
    }, 40);
    setTimeout(() => {
      segLabelbRef.current?.focus();
      segLabelbRef.current?.select();
    }, 40);


  }, [procId, segIdx, seg]);

  const flowType = statementTypeToFlowType[statementType];
  const statementOption = statementTypeOptions.find(option => option.type === statementType);
  const segmentFrom = seg?.from ?? 'origen';
  const segmentTo = seg?.to ?? 'destino';

  const buildStatement = (): SegmentStatement | null => {
    switch (statementType) {
      case 'STEP': {
        let parsedMeta: Record<string, string> = {};
        try {
          parsedMeta = JSON.parse(stepMeta || '{}');
        } catch {
          setError('Meta de STEP inválida: usa JSON válido.');
          return null;
        }
        return {
          type: 'STEP',
          from: seg?.from ?? '',
          to: seg?.to ?? '',
          action: stepAction.trim(),
          sync: stepSync,
          meta: parsedMeta,
        };
      }
      case 'ASSIGN':
        return { type: 'ASSIGN', variable: assignVariable.trim(), expression: assignExpression.trim() };
      case 'WAIT':
        return { type: 'WAIT', mode: waitMode, duration: waitDuration.trim(), condition: waitCondition.trim() };
      case 'CALL':
        return { type: 'CALL', name: callName.trim(), args: callArgs.trim() };
      case 'TRIGGER':
        return { type: 'TRIGGER', name: triggerName.trim(), args: triggerArgs.trim() };
      case 'ON':
        return { type: 'ON', event: onEvent.trim(), childBlock: onChildBlock.trim() };
      case 'EXCEPTION':
        return { type: 'EXCEPTION', error: exceptionError.trim(), childBlock: exceptionChildBlock.trim() };
      default:
        return null;
    }
  };

  const confirm = () => {
    const nextStatement = buildStatement();
    if (!nextStatement) return;

    const nextPatch = {
      friction,
      freq,
      dur,
      pred,
      flowType,
      label: segLabel,
      statement: nextStatement,
    };

    if (proc) {
      const previewSegments = resolveProcessEdges(proc, buildEdgeMap(edges)).map((segment, idx) => (
        idx === segIdx
          ? ({ ...segment, ...nextPatch } as typeof segment)
          : segment
      ));
      const validation = validateProcessSegments({ segments: previewSegments });
      if (!validation.isValid) {
        const contextualIssue = validation.issues.find(issue => issue.segmentIndex === segIdx);
        setError(contextualIssue?.message ?? validation.issues[0]?.message ?? 'La estructura del proceso quedó inválida.');
        return;
      }
    }

    setError('');
    updateSegment(procId, segIdx, nextPatch);
    onClose();
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    }
    if (e.key === 'Escape') onClose();
  };

  const fricColor: Record<Friction, string> = { none: '#4a9e6b', low: '#d6b256', medium: '#e0a800', high: '#c0392b' };
  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        background: 'white',
        border: `1.5px solid ${highlight ? '#4a9e6b' : '#ddd'}`,
        borderRadius: 12,
        padding: '20px 22px',
        boxShadow: highlight ? '0 10px 42px rgba(74,158,107,0.22)' : '0 8px 40px rgba(0,0,0,0.18)',
        zIndex: 300,
        minWidth: 280,
        width: 760,
        maxWidth: '96vw',
        maxHeight: '88vh',
        overflowY: 'auto',
      }}
      onClick={e => e.stopPropagation()}
    >



      <div style={{ ...headerCardStyle, borderColor: highlight ? '#4a9e6b' : '#ebe6db' }}>
        <div style={{ display: 'grid', gap: 4 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#8f8471', fontWeight: 600 }}>{proc?.name ?? 'Proceso'}</p>
          <p style={{ margin: 0, fontSize: 18, color: '#463f32', fontWeight: 700 }}>
            {segmentFrom} <span style={{ color: '#9a8f7c' }}>→</span> {segmentTo}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: '#8f8471' }}>
            Segmento #{segIdx + 1} · Edita statement, semántica y contexto operativo.
          </p>
        </div>
        <button type="button" onClick={onClose} style={ghostButtonStyle}>Cerrar</button>


      </div>

        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Label</p>
          <input ref={verbRef} value={segLabel} onChange={e => setSegLabel(e.target.value)} onKeyDown={handleKey} placeholder="Verbo" maxLength={300} 
          style={inputStyle} />
        </div>


      <div style={sectionStyle}>
         {(['Flujo', 'Control'] as const).map(group => (
          <div key={group} style={{ display: 'grid', gap: 1 }}>
            <p style={{ ...label, margin: 0, textAlign: 'left' }}>{group}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 4 }}>
              {statementTypeOptions
                .filter(option => option.group === group)
                .map(option => (
                  <button
                    key={option.type}
                    type="button"
                    style={btn(statementType === option.type, '#4a7fb5')}
                    onClick={() => setStatementType(option.type)}
                    title={option.helper}
                  >
                    {option.label}
                  </button>
                ))}
            </div>
          </div>
        ))}
      </div>

      {statementType === 'STEP' && (
        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Detalle STEP</p>
          <input ref={verbRef} value={stepAction} onChange={e => setStepAction(e.target.value)} onKeyDown={handleKey} placeholder="acción (STEP)" maxLength={40} style={inputStyle} />
          <div style={chipGridStyle}>
            {(['blocking', 'non-blocking'] as Blocking[]).map(b => (
              <button key={b} style={btn(stepSync === b, '#4a7fb5')} onClick={() => setStepSync(b)}>{b}</button>
            ))}
          </div>
        </div>
      )}

 
      {statementType === 'WAIT' && (
        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Detalle WAIT</p>
          <div style={chipGridStyle}>
            {(['FOR', 'UNTIL'] as const).map(mode => (
              <button key={mode} style={btn(waitMode === mode, '#9b59b6')} onClick={() => setWaitMode(mode)}>{mode}</button>
            ))}
          </div>
          {waitMode === 'FOR'
            ? <input value={waitDuration} onChange={e => setWaitDuration(e.target.value)} onKeyDown={handleKey} placeholder="duración (ej. 5m)" maxLength={40} style={inputStyle} />
            : <input value={waitCondition} onChange={e => setWaitCondition(e.target.value)} onKeyDown={handleKey} placeholder="condición (UNTIL)" maxLength={80} style={inputStyle} />}
        </div>
      )}

      {(statementType === 'CALL' || statementType === 'TRIGGER') && (
        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Detalle {statementType}</p>
          <input
            value={statementType === 'CALL' ? callName : triggerName}
            onChange={e => statementType === 'CALL' ? setCallName(e.target.value) : setTriggerName(e.target.value)}
            onKeyDown={handleKey}
            placeholder="nombre"
            maxLength={60}
            style={inputStyle}
          />
          <input
            value={statementType === 'CALL' ? callArgs : triggerArgs}
            onChange={e => statementType === 'CALL' ? setCallArgs(e.target.value) : setTriggerArgs(e.target.value)}
            onKeyDown={handleKey}
            placeholder="argumentos (csv o texto)"
            maxLength={120}
            style={inputStyle}
          />
        </div>
      )}

      {(statementType === 'ON' || statementType === 'EXCEPTION') && (
        <div style={sectionStyle}>
          <p style={sectionTitleStyle}>Detalle {statementType}</p>
          <input
            value={statementType === 'ON' ? onEvent : exceptionError}
            onChange={e => statementType === 'ON' ? setOnEvent(e.target.value) : setExceptionError(e.target.value)}
            onKeyDown={handleKey}
            placeholder={statementType === 'ON' ? 'evento' : 'error'}
            maxLength={80}
            style={inputStyle}
          />
 
        </div>
      )}

      {lastValidationWarning && (
        <div style={{ fontSize: 10, color: '#8a2d22', border: '1px solid #f0c9c2', background: '#fff3f1', borderRadius: 6, padding: '6px 8px', fontFamily: "'DM Mono',monospace", display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span>⚠ {lastValidationWarning}</span>
          <button type="button" onClick={() => setLastValidationWarning(null)} style={{ border: 'none', background: 'transparent', color: '#8a2d22', cursor: 'pointer', fontWeight: 700, padding: 0, lineHeight: 1 }} title="Cerrar advertencia">✕</button>
        </div>
      )}
      <div style={divider} />

      <p style={hintStyle}>{statementOption?.helper}</p>
      <p style={sectionTitleStyle}>Contexto del segmento</p>
      <div style={contextGridStyle}>
        <div style={sectionStyle}>
          <p style={label}>Frecuencia</p>
          <div style={chipGridStyle}>
            {(['daily', 'weekly', 'monthly', 'adhoc'] as Frequency[]).map(f => (
              <button key={f} style={btn(freq === f)} onClick={() => setFreq(f)}>
                {{ daily: 'diario', weekly: 'semanal', monthly: 'mensual', adhoc: 'cuando surja' }[f]}
              </button>
            ))}
          </div>
          <p style={hintStyle}>{helperTextByFrequency[freq]}</p>
        </div>

        <div style={sectionStyle}>
          <p style={label}>Duración habitual</p>
          <div style={chipGridStyle}>
            {(['mins', 'hours', 'days', 'weeks'] as Duration[]).map(d => (
              <button key={d} style={btn(dur === d)} onClick={() => setDur(d)}>
                {{ mins: 'mins', hours: 'horas', days: 'días', weeks: 'semanas' }[d]}
              </button>
            ))}
          </div>
        </div>

        <div style={sectionStyle}>
          <p style={label}>¿Es predecible?</p>
          <div style={chipGridStyle}>
            {(['yes', 'sometimes', 'no'] as Predict[]).map(p => (
              <button key={p} style={btn(pred === p)} onClick={() => setPred(p)}>
                {{ yes: '✓ sí', sometimes: 'a veces', no: '✗ no' }[p]}
              </button>
            ))}
          </div>
        </div>

 

        <div style={{ ...sectionStyle, gridColumn: '1 / -1' }}>
          <p style={label}>Fricción percibida</p>
          <div style={chipGridStyle}>
            {(['none', 'low', 'medium', 'high'] as Friction[]).map(f => (
              <button key={f} style={btn(friction === f, fricColor[f])} onClick={() => setFriction(f)}>
                {{ none: '🟢 sin', low: '🟡 baja', medium: '🟠 media', high: '🔴 alta' }[f]}
              </button>
            ))}
          </div>
        </div>
      </div>


      {error && <p style={{ fontSize: 10, color: '#c0392b', textAlign: 'center', margin: 0 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onClose} style={{ ...actionButtonStyle, background: '#f3f3f3', color: '#666' }}>
          Cancelar
        </button>
        <button type="button" onClick={confirm} style={{ ...actionButtonStyle, background: '#4a9e6b', color: 'white' }}>
          Guardar segmento
        </button>
      </div>
      <p style={{ fontSize: 9, color: '#bbb', textAlign: 'center', margin: 0 }}>Atajos: Enter guarda · Esc cancela</p>
    </div>
  );
};

export default SegmentEditor;
